import { PALETTE } from '@overlayqa/shared';
import type { ReactNode, ComponentType, ComponentProps } from 'react';
import React from 'react';
import {
  AuthenticateWithRedirectCallback as ClerkAuthenticateWithRedirectCallback,
  ClerkProvider,
  ClerkDegraded as ClerkDegradedComponent,
  ClerkFailed as ClerkFailedComponent,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  UserButton as ClerkUserButton,
  useAuth as clerkUseAuth,
  useClerk as clerkUseClerk,
  useReverification as clerkUseReverification,
  useSignIn as clerkUseSignIn,
  useSignUp as clerkUseSignUp,
  useUser as clerkUseUser,
} from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';
import { isClerkOriginBlocked } from './clerkOriginAllowed';

export const AUTH_CALLBACK_PATH = '/auth/callback';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * True when a PRODUCTION key is served on an origin it can never satisfy -- the
 * team-review proxy renders the dashboard bundle on the Railway proxy host, a
 * different registrable domain than the key's app domain. clerk-js cannot
 * complete its handshake there, so mounting <ClerkProvider> only produces a
 * loading screen that waits, reports a fake auth_load_failed, and offers an
 * unwinnable Retry. Read once from the current hostname; the origin never
 * changes within a session. See lib/clerkOriginAllowed.ts (fails open, so the
 * real dashboard host is never blocked).
 */
export const clerkOriginBlocked = isClerkOriginBlocked(
  publishableKey,
  typeof window === 'undefined' ? null : window.location.hostname,
);

// Gates the ClerkProvider mount AND every Clerk re-export below. On a blocked
// origin this is false, so the whole file falls back to stubs -- no provider is
// mounted, and no surface waits on a Clerk load that will never arrive.
export const authEnabled = Boolean(publishableKey) && !clerkOriginBlocked;

export function AuthProvider({ children }: { children: ReactNode }) {
  if (authEnabled) {
    return (
      <ClerkProvider
        publishableKey={publishableKey}
        signInUrl="/sign-in"
        signUpUrl="/sign-in"
        appearance={dashboardClerkAppearance}
        localization={{ userProfile: {
          navbar: { billing: 'Payment methods & invoices', description: 'Manage your account. Your team plan and trial are in Billing.' },
          billingPage: {
            start: { headerTitle__subscriptions: 'Personal subscription' },
            subscriptionsListSection: { title: 'Personal subscription' },
          },
        } }}
      >
        {children}
      </ClerkProvider>
    );
  }

  return <>{children}</>;
}

type ChildrenProps = { children?: ReactNode };

export const SignedIn: React.ComponentType<ChildrenProps> = authEnabled
  ? ClerkSignedIn
  : ({ children }: ChildrenProps) => <>{children}</>;

export const SignedOut: React.ComponentType<ChildrenProps> = authEnabled
  ? ClerkSignedOut
  : (_props: ChildrenProps) => null;

/**
 * Clerk's own load-status components (@clerk/clerk-react 5.61.3). They read
 * `isomorphicClerk.status` and render children only when it is 'error' /
 * 'degraded' respectively, which is strictly better than our hand-rolled
 * CLERK_LOAD_TIMEOUT_MS -- that constant's own comment calls it "a guess", and
 * it cannot see the DEGRADED state at all (Clerk loaded but unhealthy, which is
 * what the 2026-08-27 incident session was actually in: two auth_load_succeeded
 * between three auth_load_failed).
 *
 * Guarded on authEnabled like every other Clerk re-export in this file, and for
 * a sharper reason than the others: both call useAssertWrappedByClerkProvider
 * and THROW outside a provider. With no publishable key AuthProvider renders no
 * ClerkProvider, so an unguarded re-export would crash every auth-disabled
 * surface (local harnesses, CI) rather than degrade.
 */
export const ClerkFailed: React.ComponentType<ChildrenProps> = authEnabled
  ? ClerkFailedComponent
  : (_props: ChildrenProps) => null;

export const ClerkDegraded: React.ComponentType<ChildrenProps> = authEnabled
  ? ClerkDegradedComponent
  : (_props: ChildrenProps) => null;

export const RedirectToSignIn: React.ComponentType<Record<string, unknown>> = () => {
  const { pathname, search } = useLocation();
  const returnPath = `${pathname}${search}`;
  const to = returnPath && returnPath !== '/'
    ? `/sign-in?redirect_url=${encodeURIComponent(returnPath)}`
    : '/sign-in';
  return <Navigate to={to} replace />;
};

export const dashboardClerkAppearance = {
  variables: {
    colorPrimary: 'var(--color-primary)',
    colorText: PALETTE.neutral[700],
    colorTextSecondary: PALETTE.neutral[500],
    fontFamily: 'Plus Jakarta Sans, system-ui, -apple-system, sans-serif',
  },
  layout: {
    socialButtonsPlacement: 'top' as const,
    socialButtonsVariant: 'blockButton' as const,
    showOptionalFields: false,
    unsafe_disableDevelopmentModeWarnings: true,
  },
  elements: {
    // Checkout must cover Layout's sticky header (z-10) and FeedbackFab (z-40).
    // Style the portal root so its backdrop and drawer stay in the same layer.
    drawerRoot: { zIndex: 50 },
    card: 'border-0 bg-transparent shadow-none',
    rootBox: 'w-full',
    headerTitle: 'hidden',
    headerSubtitle: 'hidden',
    socialButtonsBlockButton:
      'h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-medium text-gray-700 shadow-none transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600',
    socialButtonsBlockButtonText: 'font-medium',
    dividerLine: 'bg-gray-200',
    dividerText: 'text-xs font-medium uppercase tracking-[0.24em] text-gray-400',
    formButtonPrimary:
      'h-11 rounded-xl border border-primary-500 bg-primary-500 text-sm font-medium text-white shadow-none transition hover:bg-primary-600',
    footerActionLink: 'font-medium text-primary-600 hover:text-primary-700',
    formFieldInput:
      'h-11 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 shadow-none focus:border-primary-500 focus:ring-primary-500',
    formFieldLabel: 'text-sm font-medium text-gray-700',
    identityPreviewText: 'text-sm text-gray-600',
    formResendCodeLink: 'font-medium text-primary-600 hover:text-primary-700',
    otpCodeFieldInput:
      'h-11 rounded-xl border border-gray-200 bg-white text-gray-900 shadow-none focus:border-primary-500 focus:ring-primary-500',
    alertText: 'text-sm text-red-700',
    alert:
      'rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 shadow-none',
    footer: 'mt-5',
    footerPagesLink: 'hidden',
    badge: 'hidden',
    poweredByClerk: 'hidden',
    // Hide "Secured by Clerk" in the avatar (UserButton) dropdown; its footer
    // holds only branding, so hiding it is safe. CSS backstop in index.css.
    userButtonPopoverFooter: 'hidden',
  },
};

const AuthDisabledNotice = ({ message }: { message: string }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-left">
    <h2 className="text-base font-semibold text-amber-900">Auth disabled</h2>
    <p className="mt-1 text-sm leading-6 text-amber-800">{message}</p>
  </div>
);

export const AuthenticateWithRedirectCallback: ComponentType<Record<string, unknown>> = authEnabled
  ? ClerkAuthenticateWithRedirectCallback
  : (_props: Record<string, unknown>) => (
      <AuthDisabledNotice message="Set VITE_CLERK_PUBLISHABLE_KEY to enable OAuth callbacks." />
    );

export type UserButtonProps = ComponentProps<typeof ClerkUserButton>;

export const UserButton: ComponentType<UserButtonProps> = authEnabled
  ? ClerkUserButton
  : (_props: UserButtonProps) => null;

// Keep Clerk's component identity: its child parser recognizes this exact
// component, so a wrapper around it would silently drop the custom page.
export const UserProfilePage = ClerkUserButton.UserProfilePage;

// Auth-disabled fallback (no publishable key). Only reached in local/CI
// harnesses — prod and local dev always set a real key. The flow-screenshot
// harness sets VITE_HARNESS_AUTH so the stub presents a signed-in local user
// with a placeholder token, which lets useApiQuery (gated on isSignedIn + a
// non-null token) run against mocked APIs. Off by default, so no other caller
// changes behavior.
const harnessAuth = Boolean(import.meta.env.VITE_HARNESS_AUTH);
// Module-level constant so `getToken` keeps a stable identity across renders,
// matching Clerk's real useAuth(). An inline object here handed every consumer
// a NEW getToken per render, so any effect keyed on it (the house pattern:
// useNotifications, CommentThread, DesignTokenSources) would re-run forever in
// stub mode. Values are static (harnessAuth is a module const), so hoisting
// changes identity only.
const AUTH_STUB = {
  isLoaded: true,
  isSignedIn: harnessAuth,
  getToken: async () => (harnessAuth ? 'harness-token' : null),
  has: () => true,
};
const useAuthStub = () => AUTH_STUB;

const useUserStub = () => ({
  user: harnessAuth
    ? {
        id: 'harness-user',
        primaryEmailAddress: { emailAddress: 'harness@overlayqa.com' },
        username: 'Harness',
        firstName: 'Harness',
        lastName: 'Tester',
        fullName: 'Harness Tester',
        imageUrl: null,
      }
    : null,
});

type UseAuthReturn = {
  userId?: string | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
  has?: (params: Record<string, unknown>) => boolean;
};

const useSignInStub = () => ({
  isLoaded: false as const,
  setActive: undefined,
  signIn: undefined,
});

const useSignUpStub = () => ({
  isLoaded: false as const,
  setActive: undefined,
  signUp: undefined,
});

export const useAuth: () => UseAuthReturn = authEnabled
  ? (clerkUseAuth as () => UseAuthReturn)
  : useAuthStub;

export const useSignIn: typeof clerkUseSignIn = authEnabled ? clerkUseSignIn : useSignInStub;
export const useSignUp: typeof clerkUseSignUp = authEnabled ? clerkUseSignUp : useSignUpStub;
export const useUser = authEnabled ? clerkUseUser : useUserStub;
// Clerk's step-up check before a sensitive account change. Without Clerk there
// is no session to re-verify, so the stub runs the action as given.
const useReverificationStub = (<T,>(action: T) => action) as unknown as typeof clerkUseReverification;
export const useReverification: typeof clerkUseReverification = authEnabled ? clerkUseReverification : useReverificationStub;

type AppClerk = Pick<ReturnType<typeof clerkUseClerk>, 'signOut' | 'closeUserProfile'> & {
  client: ReturnType<typeof clerkUseClerk>['client'] | null;
  billing?: ReturnType<typeof clerkUseClerk>['billing'];
};
const useClerkStub = (): AppClerk => ({ client: null, signOut: async () => {}, closeUserProfile: () => {} });
export const useClerk: () => AppClerk = authEnabled
  ? clerkUseClerk
  : useClerkStub;
